import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { NotFoundPage, ServiceUnavailablePage } from '@/modules/error-view';
import { ProfilePage } from '@/modules/profile';
import { UsersTablePage } from '@/modules/iam';
import { TemplatesPage } from '@/modules/templates';
import { CreateTemplatePage } from '@/modules/templates/pages/create-template/create-template';
import { EditTemplatePage } from '@/modules/templates/pages/edit-template/edit-template';
import { MediaLibraryPage } from '@/modules/media-library';
import {
  CampaignsPage,
  CampaignDetailsPage,
  CampaignContentEditorPage,
  CreateCampaignPage,
  EditCampaignPage,
  SendsPage,
} from '@/modules/campaigns';
import {
  CampaignReportPage,
  ReportsDeliverabilityPage,
  ReportsOverviewPage,
} from '@/modules/reports';
import {
  ContactsPage,
  ContactDetailsPage,
  CustomFieldsPage,
  ImportContactsPage,
  SuppressionPage,
} from '@/modules/contacts';
import { ListsPage, ListDetailsPage } from '@/modules/lists';
import { SubscriptionConfirmPage } from '@/modules/contacts/pages/subscription-confirm';
import { UnsubscribePage } from '@/modules/contacts/pages/unsubscribe';
import { SegmentsPage } from '@/modules/segments';
import {
  BrandingSettingsPage,
  LegalFooterSettingsPage,
  OptInSettingsPage,
  SettingsPage,
  SystemEmailsPage,
} from '@/modules/settings';
import { ApiKeysPage } from '@/modules/api-keys/api-keys-page';
import { AuditLogPage } from '@/modules/audit-log';
import { MainLayout } from '@/layout/main-layout/main-layout';
import { AuthRoutes } from './auth.route';
import { Guard } from '@/state/store/auth/guard';
import { ClientMiddleware } from '@/state/client-middleware';
import { ThemeProvider } from '@/styles/theme/theme-provider';
import { SidebarProvider } from '@/components/ui-kit/sidebar';
import { Toaster } from '@/components/ui-kit/toaster';
import { useLanguageContext } from '@/i18n/language-context';
import { LoadingOverlay } from '@/components/core';

const LegacyListRedirect = () => {
  const { listId } = useParams();
  return <Navigate to={`/lists/${listId}`} replace />;
};

export const AppRoutes = () => {
  const { isInitialLoading } = useLanguageContext();

  if (isInitialLoading) {
    return <LoadingOverlay />;
  }
  return (
    <div className="min-h-screen bg-background font-sans antialiased relative">
      <ClientMiddleware>
        <ThemeProvider>
          <SidebarProvider>
            <Routes>
              {AuthRoutes}
              <Route
                element={
                  <Guard>
                    <MainLayout />
                  </Guard>
                }
              >
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/identity-management" element={<UsersTablePage />} />
                <Route path="/templates" element={<TemplatesPage />} />
                <Route path="/templates/create" element={<CreateTemplatePage />} />
                <Route path="/templates/edit/:templateId" element={<EditTemplatePage />} />
                <Route path="/media-library" element={<MediaLibraryPage />} />
                <Route path="/media" element={<Navigate to="/media-library" replace />} />
                <Route path="/campaigns" element={<CampaignsPage />} />
                <Route path="/campaigns/create-campaign" element={<CreateCampaignPage />} />
                <Route path="/campaigns/edit/:campaignId" element={<EditCampaignPage />} />
                <Route
                  path="/campaigns/:campaignId/content"
                  element={<CampaignContentEditorPage />}
                />
                <Route path="/campaigns/:campaignId" element={<CampaignDetailsPage />} />
                <Route path="/contacts" element={<ContactsPage />} />
                <Route path="/contacts/import" element={<ImportContactsPage />} />
                <Route
                  path="/contacts/custom-fields"
                  element={<Navigate to="/custom-fields" replace />}
                />
                <Route path="/contacts/:contactId" element={<ContactDetailsPage />} />
                <Route path="/custom-fields" element={<CustomFieldsPage />} />
                <Route path="/lists" element={<ListsPage />} />
                <Route path="/lists/:listId" element={<ListDetailsPage />} />
                <Route path="/segments" element={<SegmentsPage />} />
                <Route path="/lists-segments" element={<Navigate to="/lists" replace />} />
                <Route path="/lists-segments/:listId" element={<LegacyListRedirect />} />
                <Route path="/suppression" element={<SuppressionPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/api-keys" element={<ApiKeysPage />} />
                <Route path="/audit-log" element={<AuditLogPage />} />
                <Route path="/settings/audit-log" element={<Navigate to="/audit-log" replace />} />
                <Route path="/settings/opt-in" element={<OptInSettingsPage />} />
                <Route path="/settings/system-emails" element={<SystemEmailsPage />} />
                <Route path="/settings/branding" element={<BrandingSettingsPage />} />
                <Route path="/settings/legal-footer" element={<LegalFooterSettingsPage />} />
                <Route path="/settings/sending" element={<Navigate to="/sends" replace />} />
                <Route path="/sends" element={<SendsPage />} />
                <Route path="/reports" element={<Navigate to="/reports/overview" replace />} />
                <Route path="/reports/overview" element={<ReportsOverviewPage />} />
                <Route path="/reports/deliverability" element={<ReportsDeliverabilityPage />} />
                <Route
                  path="/reports/campaigns/:campaignId"
                  element={<CampaignReportPage tab="performance" />}
                />
                <Route
                  path="/reports/campaigns/:campaignId/links"
                  element={<CampaignReportPage tab="links" />}
                />
                <Route
                  path="/reports/campaigns/:campaignId/deliverability"
                  element={<CampaignReportPage tab="deliverability" />}
                />
                <Route path="/503" element={<ServiceUnavailablePage />} />
                <Route path="/404" element={<NotFoundPage />} />
              </Route>

              {/* Public routes (no auth required) */}
              <Route path="/subscription-confirm" element={<SubscriptionConfirmPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />

              {/* Redirects */}
              <Route path="/" element={<Navigate to="/contacts" />} />

              <Route path="*" element={<Navigate to="/404" />} />
            </Routes>
          </SidebarProvider>
        </ThemeProvider>
      </ClientMiddleware>
      <Toaster />
    </div>
  );
};
